/**
 * ScamBlocker AI — Ses Kayıtları (/recordings)
 * KVKK: oynatma için reason_code zorunlu; presigned URL ile dinleme.
 */

import { useQuery } from '@tanstack/react-query';
import { Mic, Play, ShieldCheck } from 'lucide-react';
import styles from '@/features/_shared/page.module.css';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';

interface Recording {
  id: string;
  org_call_id?: string;
  agent_id?: string;
  caller_masked?: string;
  risk_level?: string;
  duration_seconds?: number;
  consent_obtained: boolean;
  created_at: string;
  retention_expires_at?: string;
}

function fmtDur(s?: number) {
  if (!s) return '—';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

async function play(id: string) {
  const reason = window.prompt(
    'KVKK denetimi — dinleme gerekçesi (reason_code):',
    'incident_review',
  );
  if (!reason) return;
  try {
    const { data } = await apiClient.get(API.org.playRecord(id), {
      params: { reason_code: reason },
    });
    if (data.play_url) window.open(data.play_url, '_blank', 'noopener');
  } catch {
    window.alert('Oynatma URL alınamadı (depolama yapılandırılmamış olabilir).');
  }
}

export function RecordingsPage() {
  const { data, isLoading, isError } = useQuery<{ recordings: Recording[] }>({
    queryKey: ['recordings'],
    queryFn: async () => (await apiClient.get(API.org.recordings)).data,
  });

  const recs = data?.recordings ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Mic size={22} className={styles.titleIcon} />
        <h1 className={styles.title}>Ses Kayıtları</h1>
        {data && <span className={styles.countPill}>{recs.length} kayıt</span>}
      </div>

      <div className={styles.tableWrapper}>
        {isLoading && (
          <div className={styles.center}>
            <span className={styles.spinner} />
          </div>
        )}
        {isError && <div className={styles.errorBox}>Kayıtlar yüklenemedi.</div>}
        {!isLoading && !isError && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Numara</th>
                <th>Agent</th>
                <th>Süre</th>
                <th>Risk</th>
                <th>Onam</th>
                <th>Saklama Bitişi</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {recs.map((r) => (
                <tr key={r.id} className={styles.row}>
                  <td className={styles.mono}>{new Date(r.created_at).toLocaleString('tr-TR')}</td>
                  <td className={styles.mono}>{r.caller_masked ?? '—'}</td>
                  <td>{r.agent_id ?? '—'}</td>
                  <td className={styles.mono}>{fmtDur(r.duration_seconds)}</td>
                  <td>{r.risk_level ?? '—'}</td>
                  <td>
                    {r.consent_obtained ? (
                      <span className={styles.badge} style={{ color: 'var(--color-success)' }}>
                        <ShieldCheck size={12} /> Var
                      </span>
                    ) : (
                      <span className={styles.badge} style={{ color: 'var(--color-warning)' }}>
                        Yok
                      </span>
                    )}
                  </td>
                  <td className={styles.mono}>
                    {r.retention_expires_at
                      ? new Date(r.retention_expires_at).toLocaleDateString('tr-TR')
                      : '—'}
                  </td>
                  <td>
                    <button type="button" className={styles.actionBtn} onClick={() => play(r.id)}>
                      <Play size={14} /> Dinle
                    </button>
                  </td>
                </tr>
              ))}
              {recs.length === 0 && (
                <tr>
                  <td colSpan={8} className={styles.empty}>
                    Kayıt bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
